import { css, html, LitElement, nothing } from "lit";
import type { TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type {
    CommerceCartPurchaseType,
    CommerceCheckout,
    CommerceProductComponent,
    CommerceRecurringFrequencyOption,
} from "@teqtank/commerce-sdk";
import "./teqtank-price.js";

@customElement("teqtank-checkout-summary")
export class TeqtankCheckoutSummary extends LitElement {
    @property({ attribute: false }) public accessor checkout: CommerceCheckout | undefined;
    @property({ type: Boolean }) public accessor busy = false;
    @property({ type: Boolean }) public accessor calculating = false;
    @property({ type: Boolean, attribute: "calculated" })
    public accessor calculated = false;
    @property({ attribute: false }) public accessor calculationErrors: readonly string[] = [];
    @property({ attribute: false }) public accessor recurringSavings: Readonly<
        Record<
            string,
            {
                saving?: string;
                oneTimePrice: string;
                recurringPrice: string;
                frequencyOptions: readonly CommerceRecurringFrequencyOption[];
                defaultFrequencyId: string;
                defaultFrequencyDays: number;
            }
        >
    > = {};
    @state() private accessor couponCode = "";

    public static override styles = css`
        :host {
            --tt-ink: var(--teqtank-color-text, #16221d);
            --tt-muted: var(--teqtank-color-text-muted, #6b746e);
            --tt-accent: var(--teqtank-color-accent, #728f4f);
            --tt-soft: var(--teqtank-color-surface-soft, #eee8df);
            --tt-line: var(--teqtank-color-border, rgba(22, 34, 29, 0.12));
            --tt-display: var(--teqtank-font-heading, Georgia, serif);
            --tt-body: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
            display: block;
            container-type: inline-size;
            container-name: checkout-summary;
            color: var(--tt-ink);
            font-family: var(--tt-body);
        }

        .summary {
            height: max-content;
            max-height: var(--teqtank-checkout-summary-max-height, none);
            box-sizing: border-box;
            padding: 35px;
            display: flex;
            flex-direction: column;
            overflow-y: auto;
            overscroll-behavior: contain;
            scrollbar-gutter: stable;
            background: var(--teqtank-checkout-summary-background, var(--tt-soft));
            border-radius: 4px;
        }

        .head,
        .total-line {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            min-width: 0;
        }

        .head {
            align-items: center;
        }

        .head,
        .promo-entry,
        .calculation-errors,
        .muted,
        .coupons,
        .totals {
            flex: 0 0 auto;
        }

        .head h2 {
            margin: 0;
            font: 500 32px var(--tt-display);
        }

        .count {
            font-size: 11px;
            opacity: 0.65;
        }

        .items {
            margin-top: 0.75rem;
            flex: 0 0 auto;
        }

        .checkout-line {
            padding: 19px 0;
            display: grid;
            grid-template-columns: 76px minmax(0, 1fr);
            gap: 14px;
            border-bottom: 1px solid var(--tt-line);
        }

        .checkout-line > img,
        .placeholder {
            width: 76px;
            height: 88px;
            box-sizing: border-box;
            padding: 8px;
            border-radius: 9px;
            background: #f0eee7;
            object-fit: var(--teqtank-product-image-fit, contain);
        }

        .checkout-line-main {
            position: relative;
            min-width: 0;
        }

        .checkout-line-main > div:first-child {
            padding-right: 80px;
        }

        .checkout-line-main strong,
        .checkout-line-main small {
            display: block;
        }

        .checkout-line-main > div:first-child strong {
            font-size: 12px;
        }

        .checkout-line-main > div:first-child small {
            margin-top: 3px;
            color: var(--tt-muted);
            font-size: 9px;
        }

        .components {
            margin: 0.5rem 0 0;
            padding-left: 1rem;
            color: var(--tt-muted);
            font-size: 0.72rem;
        }
        .components li {
            margin-top: 0.15rem;
        }
        .components .components {
            margin-top: 0.15rem;
        }

        .checkout-line-price {
            position: absolute;
            right: 0;
            top: 0;
            text-align: right;
        }

        .checkout-line-price s,
        .checkout-line-price b,
        .checkout-line-price small {
            display: block;
        }

        .checkout-line-price s {
            color: var(--tt-muted);
            font-size: 9px;
        }

        .checkout-line-price b {
            font-size: 12px;
        }

        .checkout-line-price small {
            margin-top: 2px;
            color: #b45e3d;
            font-size: 8px;
            font-weight: 700;
        }

        .checkout-line-actions {
            margin-top: 12px;
        }

        .subscription-switch {
            padding: 10px;
            display: grid;
            grid-template-columns: auto 1fr;
            gap: 9px;
            border: 1px solid var(--tt-line);
            border-radius: 9px;
            cursor: pointer;
            background: rgba(255, 255, 255, 0.35);
        }

        .subscription-switch.active {
            border-color: var(--tt-accent);
            background: color-mix(in srgb, var(--tt-accent) 8%, #fff);
        }

        .subscription-switch input {
            width: auto;
            margin: 2px 0 0;
        }

        .subscription-switch span strong,
        .subscription-switch span small {
            display: block;
        }

        .subscription-switch span strong {
            font-size: 11px;
        }

        .subscription-switch span small {
            margin-top: 2px;
            color: var(--tt-muted);
            font-size: 9px;
            line-height: 1.35;
        }

        .checkout-line-actions select {
            width: 100%;
            margin-top: 8px;
            padding: 9px;
            border: 1px solid var(--tt-line);
            border-radius: 8px;
            background: #fff;
            color: inherit;
            font: inherit;
            font-size: 11px;
        }

        .promotion {
            display: inline-flex;
            margin-top: 8px;
            padding: 4px 7px;
            border-radius: 999px;
            background: #dff3e6;
            color: #225c36;
            font-size: 8px;
            font-weight: 800;
            letter-spacing: 0.04em;
            text-transform: uppercase;
        }

        .promo-entry {
            margin: 20px 0 0;
            display: flex;
        }

        .promo-entry input {
            min-width: 0;
            flex: 1;
            padding: 12px;
            border: 1px solid #bbb;
            background: #fff;
            color: inherit;
            font: inherit;
        }

        .promo-entry button {
            padding: 0 14px;
            border: 0;
            background: var(--tt-ink);
            color: #fff;
            cursor: pointer;
            font: inherit;
            font-size: 11px;
            font-weight: 700;
        }

        .promo-entry button:disabled {
            opacity: 0.55;
            cursor: wait;
        }

        .calculation-errors {
            margin: 18px 0 0;
            padding: 14px 16px;
            border: 1px solid color-mix(in srgb, #9b2e1f 35%, transparent);
            background: #fff2ef;
            color: #9b2e1f;
            font-size: 0.82rem;
        }

        .calculation-errors p {
            margin: 0;
        }

        .calculation-errors p + p {
            margin-top: 0.45rem;
        }

        .coupons {
            display: grid;
            gap: 6px;
            margin-top: 10px;
        }

        .coupon-row {
            display: flex;
            justify-content: space-between;
            gap: 10px;
            align-items: flex-start;
            font-size: 9px;
        }

        .coupon-row strong {
            display: block;
            font-size: 10px;
        }

        .coupon-row span span {
            color: var(--tt-muted);
        }

        .coupon-row button {
            padding: 0;
            border: 0;
            border-bottom: 1px solid currentColor;
            background: transparent;
            color: inherit;
            cursor: pointer;
            font-size: 9px;
        }

        .totals {
            position: relative;
            margin-top: 20px;
            padding-top: 12px;
            border-top: 1px solid var(--tt-line);
        }

        .totals-loading {
            min-height: 7rem;
            display: grid;
            place-items: center;
            gap: 0.65rem;
            color: var(--tt-muted);
            font-size: 0.75rem;
            text-align: center;
        }

        .totals.loading::after {
            content: "Updating totals…";
            position: absolute;
            inset: 0;
            display: grid;
            place-items: center;
            border-radius: 0.35rem;
            background: color-mix(in srgb, var(--tt-soft) 82%, transparent);
            color: var(--tt-ink);
            font-size: 0.75rem;
            font-weight: 800;
            letter-spacing: 0.02em;
        }

        .spinner {
            width: 1.35rem;
            height: 1.35rem;
            border: 2px solid color-mix(in srgb, var(--tt-accent) 24%, transparent);
            border-top-color: var(--tt-accent);
            border-radius: 50%;
            animation: spin 700ms linear infinite;
        }

        @keyframes spin {
            to {
                transform: rotate(360deg);
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .spinner {
                animation-duration: 1.8s;
            }
        }

        .total-line {
            align-items: baseline;
            padding: 6px 0;
            font-size: 11px;
        }

        .total-line > :first-child {
            min-width: 0;
        }

        .total-line > teqtank-price,
        .total-line > strong {
            flex: 0 0 auto;
            white-space: nowrap;
        }

        .saving {
            color: var(--tt-accent);
        }

        .tax-breakdown {
            color: var(--tt-muted);
            font-size: 9px;
        }

        .grand {
            margin-top: 10px;
            padding-top: 15px;
            border-top: 1px solid var(--tt-line);
            font-size: 21px;
            font-weight: 700;
        }

        .empty {
            padding: 2rem 0;
            text-align: center;
            opacity: 0.7;
        }

        @container checkout-summary (max-width: 560px) {
            .summary {
                padding: 24px 20px;
            }

            .head h2 {
                font-size: 28px;
            }

            .checkout-line {
                grid-template-columns: 62px minmax(0, 1fr);
                gap: 11px;
                padding: 16px 0;
            }

            .checkout-line > img,
            .placeholder {
                width: 62px;
                height: 74px;
            }

            .checkout-line-main > div:first-child {
                padding-right: 0;
            }

            .checkout-line-price {
                position: static;
                margin-top: 6px;
                text-align: left;
            }

            .checkout-line-price s,
            .checkout-line-price b,
            .checkout-line-price small {
                display: inline;
                margin-right: 5px;
            }

            .promo-entry {
                display: grid;
                grid-template-columns: minmax(0, 1fr) auto;
            }
        }

        @container checkout-summary (max-width: 380px) {
            .summary {
                padding: 20px 16px;
            }

            .promo-entry {
                grid-template-columns: 1fr;
                gap: 7px;
            }

            .promo-entry button {
                min-height: 42px;
            }
        }
    `;

    protected override render() {
        const checkout = this.checkout;

        if (checkout === undefined) {
            return html`<aside class="summary" part="summary">
                <div class="empty" part="empty">
                    ${this.busy ? "Loading your bag…" : "Your bag is ready when you are."}
                </div>
            </aside>`;
        }

        const itemCount = checkout.items.reduce((sum, item) => sum + item.quantity, 0);

        return html`<aside class="summary" part="summary">
            <div class="head" part="header">
                <h2 part="title">Your bag</h2>
                <span class="count" part="count"
                    >${itemCount} ${itemCount === 1 ? "item" : "items"}</span
                >
            </div>

            <div class="items" part="item-list">
                ${checkout.items.map((item) => {
                    const subscribed = item.purchaseType === "recurring";
                    const isCartLine = item.source === "cart";
                    const recurringSaving = this.recurringSavings[item.id];

                    return html`
                        <article class="checkout-line" part="item">
                            ${
                                item.image === null
                                    ? html`<div class="placeholder" part="item-media"></div>`
                                    : html`<img
                                          part="item-media"
                                          src=${item.image.url}
                                          alt=${item.image.alternativeText}
                                      />`
                            }

                            <div class="checkout-line-main" part="item-content">
                                <div part="item-copy">
                                    <strong part="item-title">${item.name}</strong>
                                    <small part="item-meta"
                                        >${
                                            item.displaySku === undefined
                                                ? ""
                                                : `${item.displaySku} · `
                                        }Qty
                                        ${item.quantity}</small
                                    >
                                    ${this.renderComponents(item.components)}
                                </div>

                                <div
                                    class="checkout-line-price ${subscribed ? "subscribed" : ""}"
                                    part="item-price"
                                >
                                    ${
                                        subscribed && recurringSaving !== undefined
                                            ? html`<s>${recurringSaving.oneTimePrice}</s>`
                                            : nothing
                                    }
                                    <b>${item.lineTotal.formatted}</b>
                                    ${
                                        subscribed && recurringSaving?.saving !== undefined
                                            ? html`<small
                                                  >You save ${recurringSaving.saving} every
                                                  delivery</small
                                              >`
                                            : nothing
                                    }
                                </div>

                                ${
                                    item.purchaseTypeLocked
                                        ? html`<span class="promotion"
                                              >🔒
                                              ${item.purchaseTypeLockReason ?? "Purchase option fixed for enrollment"}</span
                                          >`
                                        : !isCartLine
                                          ? html`<span class="promotion"
                                                >${item.promotionCode ?? "Promotion"} · Added by
                                                calculation</span
                                            >`
                                          : subscribed || recurringSaving !== undefined
                                            ? this.renderPurchaseControls(
                                                  item.id,
                                                  item.purchaseType,
                                                  item.frequency,
                                                  item.frequencyDays,
                                              )
                                            : nothing
                                }
                            </div>
                        </article>
                    `;
                })}
            </div>

            ${
                this.calculationErrors.length > 0
                    ? html`<div class="calculation-errors" part="error" role="alert">
                          ${this.calculationErrors.map((message) => html`<p>${message}</p>`)}
                      </div>`
                    : nothing
            }

            <div class="promo-entry" part="promo">
                <input
                    part="promo-input"
                    aria-label="Promo or referral code"
                    ?disabled=${this.busy || !this.calculated}
                    .value=${this.couponCode}
                    @input=${(event: InputEvent) => {
                        this.couponCode = (event.target as HTMLInputElement).value;
                    }}
                    @keydown=${(event: KeyboardEvent) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            this.applyCoupon();
                        }
                    }}
                    placeholder="Promo or referral code"
                />
                <button
                    part="promo-action"
                    type="button"
                    ?disabled=${this.busy || !this.calculated}
                    @click=${this.applyCoupon}
                >
                    Apply
                </button>
            </div>

            ${
                !this.calculated && !this.calculating && this.calculationErrors.length === 0
                    ? html`<p class="muted" part="notice">
                          Complete the contact and delivery address to calculate shipping,
                          promotions, tax and the order total.
                      </p>`
                    : nothing
            }
            ${
                checkout.cart.coupons.length > 0
                    ? html`<div class="coupons" part="coupons">
                          ${checkout.cart.coupons.map(
                              (coupon) =>
                                  html`<div class="coupon-row">
                                      <span
                                          ><strong>${coupon.code}</strong
                                          ><span>${coupon.description}</span></span
                                      >
                                      <button
                                          type="button"
                                          ?disabled=${this.busy}
                                          @click=${() => this.removeCoupon(coupon.code)}
                                      >
                                          Remove
                                      </button>
                                  </div>`,
                          )}
                      </div>`
                    : nothing
            }
            ${
                this.calculating && !this.calculated
                    ? html`<div
                          class="totals-loading"
                          part="totals-loading"
                          role="status"
                          aria-live="polite"
                      >
                          <span class="spinner" aria-hidden="true"></span>
                          <span>Updating shipping and order totals…</span>
                      </div>`
                    : nothing
            }
            ${
                this.calculated
                    ? html`<div
                          class="totals ${this.calculating ? "loading" : ""}"
                          part="totals"
                          aria-busy=${this.calculating ? "true" : "false"}
                      >
                          <div class="total-line" part="subtotal">
                              <span>Subtotal</span>
                              <teqtank-price .value=${checkout.totals.subtotal}></teqtank-price>
                          </div>

                          ${
                              checkout.totals.discount.amount > 0
                                  ? html`<div class="total-line saving" part="discount">
                                        <span>Offers</span>
                                        <span>−${checkout.totals.discount.formatted}</span>
                                    </div>`
                                  : nothing
                          }

                          <div class="total-line" part="shipping">
                              <span>Shipping</span>
                              ${
                                  checkout.totals.shipping.amount === 0
                                      ? html`<strong>FREE</strong>`
                                      : html`<teqtank-price
                                            .value=${checkout.totals.shipping}
                                        ></teqtank-price>`
                              }
                          </div>

                          <div class="total-line tax-breakdown" part="item-tax">
                              <span>Item tax</span>
                              <teqtank-price .value=${checkout.totals.itemTax}></teqtank-price>
                          </div>

                          <div class="total-line tax-breakdown" part="shipping-tax">
                              <span>Shipping tax</span>
                              <teqtank-price .value=${checkout.totals.shippingTax}></teqtank-price>
                          </div>

                          <div class="total-line grand" part="total">
                              <span>Total</span>
                              <teqtank-price .value=${checkout.totals.total}></teqtank-price>
                          </div>
                      </div>`
                    : nothing
            }
        </aside>`;
    }

    private renderComponents(
        components: readonly CommerceProductComponent[] | null | undefined,
    ): TemplateResult | typeof nothing {
        if (!components?.length) return nothing;
        return html`
            <div aria-label="Kit contents">
                <small>Includes</small>
                <ul class="components">
                    ${components.map(
                        (component) => html`
                            <li>
                                ${component.quantity} × ${component.name}
                                ${this.renderComponents(component.components)}
                            </li>
                        `,
                    )}
                </ul>
            </div>
        `;
    }

    private renderPurchaseControls(
        lineId: string,
        purchaseType: CommerceCartPurchaseType,
        frequency?: CommerceRecurringFrequencyOption,
        frequencyDays?: number,
    ) {
        const subscribed = purchaseType === "recurring";
        const recurringSaving = this.recurringSavings[lineId];

        return html`<div class="checkout-line-actions" part="purchase-controls">
            <label class="subscription-switch ${subscribed ? "active" : ""}" part="purchase-toggle">
                <input
                    type="checkbox"
                    ?checked=${subscribed}
                    ?disabled=${this.busy}
                    @change=${(event: Event) => {
                        const checked = (event.target as HTMLInputElement).checked;
                        this.changePurchase(
                            lineId,
                            checked ? "recurring" : "one-time",
                            checked
                                ? (frequency ??
                                      recurringSaving?.frequencyOptions.find(
                                          (option) =>
                                              option.id === recurringSaving.defaultFrequencyId,
                                      ) ??
                                      recurringSaving?.frequencyOptions.find(
                                          (option) => option.days === frequencyDays,
                                      ))
                                : undefined,
                        );
                    }}
                />
                <span>
                    <strong>${subscribed ? "Subscription active" : "Subscribe & save"}</strong>
                    <small
                        >${
                            subscribed
                                ? `${recurringSaving?.saving === undefined ? "" : `Save ${recurringSaving.saving} every delivery · `}${this.frequencyDescription(frequency, recurringSaving, frequencyDays)}`
                                : recurringSaving === undefined
                                  ? "Switch this item to recurring delivery and use the recurring price returned by checkout."
                                  : recurringSaving.saving === undefined
                                    ? `${recurringSaving.recurringPrice} recurring`
                                    : `${recurringSaving.recurringPrice} recurring · save ${recurringSaving.saving} every delivery`
                        }</small
                    >
                </span>
            </label>

            ${
                subscribed
                    ? html`<select
                          aria-label="Delivery frequency"
                          ?disabled=${this.busy}
                          .value=${
                              frequency?.id ??
                              recurringSaving?.frequencyOptions.find(
                                  (option) => option.days === frequencyDays,
                              )?.id ??
                              recurringSaving?.defaultFrequencyId ??
                              ""
                          }
                          @change=${(event: Event) =>
                              this.changePurchase(
                                  lineId,
                                  "recurring",
                                  recurringSaving?.frequencyOptions.find(
                                      (option) =>
                                          option.id === (event.target as HTMLSelectElement).value,
                                  ),
                              )}
                      >
                          ${(recurringSaving?.frequencyOptions ?? []).map(
                              (option) =>
                                  html`<option value=${option.id}>${option.description}</option>`,
                          )}
                      </select>`
                    : nothing
            }
        </div>`;
    }

    private frequencyDescription(
        frequency: CommerceRecurringFrequencyOption | undefined,
        recurring:
            | {
                  frequencyOptions: readonly CommerceRecurringFrequencyOption[];
                  defaultFrequencyId: string;
                  defaultFrequencyDays: number;
              }
            | undefined,
        frequencyDays: number | undefined,
    ): string {
        if (frequency !== undefined) return frequency.description;
        const selectedDays = frequencyDays ?? recurring?.defaultFrequencyDays;
        return (
            recurring?.frequencyOptions.find((option) => option.days === selectedDays)
                ?.description ??
            (selectedDays === undefined ? "Recurring delivery" : `Every ${selectedDays} days`)
        );
    }

    private readonly applyCoupon = (): void => {
        if (!this.calculated) return;
        const code = this.couponCode.trim();

        if (!code) {
            return;
        }

        this.dispatchEvent(
            new CustomEvent("teqtank-checkout-coupon-apply", {
                detail: { code },
                bubbles: true,
                composed: true,
            }),
        );
        this.couponCode = "";
    };

    private removeCoupon(code: string): void {
        this.dispatchEvent(
            new CustomEvent("teqtank-checkout-coupon-remove", {
                detail: { code },
                bubbles: true,
                composed: true,
            }),
        );
    }

    private changePurchase(
        lineId: string,
        purchaseType: CommerceCartPurchaseType,
        frequency?: CommerceRecurringFrequencyOption,
    ): void {
        this.dispatchEvent(
            new CustomEvent("teqtank-checkout-purchase-change", {
                detail: {
                    lineId,
                    purchaseType,
                    ...(frequency === undefined
                        ? {}
                        : { frequency, frequencyId: frequency.id, frequencyDays: frequency.days }),
                },
                bubbles: true,
                composed: true,
            }),
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-checkout-summary": TeqtankCheckoutSummary;
    }
}
